import { useRef } from 'react';
import type Konva from 'konva';
import { getNodeMutationManager } from '../../../core/commands/mutations/NodeMutationManager';
import { RotateNodeCommand } from '../../../core/commands/TransformCommands';
import { computeMarriageBaselineY } from '../../../core/connectors/utils/marriageGeometry';
import { computeConnectionSnap, getClosestNodeAnchor, getNodeAnchorCandidates, resolveConnectionGeometryNode } from '../lib/connectionSnap';
import { toCanvasCoordinates } from '../lib/stageInteractions';
import {
  executeCanvasConnectionPlan,
  executeIncompleteCanvasConnection,
  planCanvasConnectionCommit,
} from '../lib/commitCanvasConnection';
import { AlignmentGuidesService } from '../../../domains/diagram/services/alignment/AlignmentGuidesService';
import { DistributionGuidesService } from '../../../domains/diagram/services/alignment/DistributionGuidesService';
import type { AlignmentGuideCandidate, AlignmentLine } from '../../../domains/diagram/models/AlignmentGuides';
import type { DistributionGapSegment } from '../../../domains/diagram/models/DistributionGuides';
import type { Bounds } from '../../../core/shapes/models/ShapeViewModel';
import { getGenogramPersonSymbolRect } from '../lib/genogramPersonSymbolBounds';
import { resolveFrameCreationDiagramType } from '../lib/diagramTypeResolution';
import { useLabelSettingsStore } from '../../../stores/labelSettingsStore';
import type { PersonLabelGeometryContext } from '../../../lib/diagram/personLabelBounds';

const getNativeClientPoint = (
  event: MouseEvent | TouchEvent,
): { x: number; y: number } | null => {
  if ('touches' in event) {
    const touch = event.touches[0] ?? event.changedTouches[0];
    return touch ? { x: touch.clientX, y: touch.clientY } : null;
  }
  return Number.isFinite(event.clientX) && Number.isFinite(event.clientY)
    ? { x: event.clientX, y: event.clientY }
    : null;
};

const PRIMARY_BACKGROUND_PAN_THRESHOLD_PX = 4;

const pickPositiveNumber = (...values: Array<unknown>): number | undefined => {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
      return value;
    }
  }
  return undefined;
};

const resolveNodeRect = (node: any): { x: number; y: number; w: number; h: number } => {
  const attrs = ((node?.attrs ?? {}) as Record<string, unknown>);
  const w = pickPositiveNumber(
    node?.rectW,
    node?.w,
    node?.width,
    attrs.rectW,
    attrs.w,
    attrs.width,
  ) ?? (node?.type === 'image' ? 240 : 60);
  const h = pickPositiveNumber(
    node?.rectH,
    node?.h,
    node?.height,
    attrs.rectH,
    attrs.h,
    attrs.height,
  ) ?? (node?.type === 'image' ? 160 : 60);
  const x = pickPositiveNumber(node?.rectW, attrs.rectW) !== undefined
    ? ((node?.rectX ?? attrs.rectX ?? node?.x ?? 0) as number)
    : ((node?.x ?? 0) as number);
  const y = pickPositiveNumber(node?.rectH, attrs.rectH) !== undefined
    ? ((node?.rectY ?? attrs.rectY ?? node?.y ?? 0) as number)
    : ((node?.y ?? 0) as number);
  return getGenogramPersonSymbolRect(node, { x, y, w, h }) ?? { x, y, w, h };
};

export function useCanvasStagePointerHandlers(input: {
  stageRef: { current: Konva.Stage | null };
  layerRef: { current: Konva.Layer | null };
  nodeRefs: { current: Map<string, Konva.Node> };
  frameGroupRefs: { current: Map<string, Konva.Node> };

  toolRegistry: any;
  canWrite: boolean;

  clearActiveComment: () => void;

  isPanMode: boolean;
  panOnPrimaryCanvasDrag?: boolean;
  isPanning: boolean;
  setIsPanning: (next: boolean) => void;
  panStart: { x: number; y: number };
  setPanStart: (next: { x: number; y: number }) => void;

  isSelecting: boolean;
  setIsSelecting: (next: boolean) => void;
  selectionRect: { x: number; y: number; width: number; height: number; visible: boolean };
  setSelectionRect: (next: {
    x: number;
    y: number;
    width: number;
    height: number;
    visible: boolean;
  }) => void;

  setSelected: (next: Set<string>) => void;
  setUserSelection?: (ids: string[]) => void;

  placePreview: { visible: boolean };

  frameTool: any;
  frameManager: any;
  setSelectedFrameId: (next: string | null) => void;

  isConnecting: boolean;
  setIsConnecting: (next: boolean) => void;
  firstConnectNode: string | null;
  setFirstConnectNode: (next: string | null) => void;

  hoveredMarriageBar:
    | { x1: number; x2: number; y: number; marriageEdgeId: string; parent1Id: string; parent2Id: string }
    | null;
  setHoveredMarriageBar: (
    next:
      | { x1: number; x2: number; y: number; marriageEdgeId: string; parent1Id: string; parent2Id: string }
      | null,
  ) => void;

  hoveredTargetId: string | null;
  setHoveredTargetId: (next: string | null) => void;
  setHoveredTargetAnchor: (next: 'top' | 'right' | 'bottom' | 'left' | null) => void;
  setHoveredTargetPoint: (next: { x: number; y: number } | null) => void;
  setHoveredTargetAnchors: (
    next: Array<{ id: string; side: 'top' | 'right' | 'bottom' | 'left'; pt: { x: number; y: number } }>,
  ) => void;

  connectionPreview: { startX: number; startY: number; endX: number; endY: number; visible: boolean };
  setConnectionPreview: (
    next:
      | { startX: number; startY: number; endX: number; endY: number; visible: boolean }
      | ((prev: { startX: number; startY: number; endX: number; endY: number; visible: boolean }) => {
        startX: number;
        startY: number;
        endX: number;
        endY: number;
        visible: boolean;
      }),
  ) => void;

  connectorTool: any;
  previewStartAnchorRef: { current: 'top' | 'right' | 'bottom' | 'left' | null };
  previewEndAnchorRef: { current: 'top' | 'right' | 'bottom' | 'left' | null };
  autoStartAnchorRef: { current: boolean };
  freeConnectionStartPointRef: { current: { x: number; y: number } | null };
  beginConnectingFromNode?: (sourceNodeId: string, pointer?: { x: number; y: number }) => boolean;

  getSnapCandidatesForSourceNode: (sourceId: string | null) => any[];
  nodes: any[];
  edges: any[];
  interactiveNodes: any[];
  renderedEdges: any[];
  diagramType?: any;

  isEndpointDragging: boolean;

  effectiveDiagramType: any;
  commandManager: any;
  doc: any;
  canvasPluginsRef: { current: any[] };
  connectionCommitExtra?: Record<string, unknown>;
  ensureOrgDeps: () => Promise<any>;

  isRotating: boolean;
  setIsRotating: (next: boolean) => void;
  rotateTargetIdRef: { current: string | null };
  rotateStartAngleRef: { current: number };
  rotateInitialRotationRef: { current: number };
  rotatePreviewRef: { current: number };
  rotateInnerRef: { current: Konva.Node | null };
  rotateCenterRef: { current: { cx: number; cy: number } };
  rotateRafRef: { current: number | null };
  rotateHandleGroupRef: { current: Konva.Container | null };
  updateRotateHandlePosition: (angle: number) => void;
  scheduleDirtyForNode: (nodeId: string) => void;
  previewViewportStage: (next: { scale: number; offsetX: number; offsetY: number }) => void;
  getLatestViewportStage: () => { scale: number; offsetX: number; offsetY: number };
  commitViewportStageImmediate: (next: { scale: number; offsetX: number; offsetY: number }) => void;

  alignmentGuidesEnabled?: boolean;
  distributionGuidesEnabled?: boolean;
  rotationStaticCandidatesRef?: { current: AlignmentGuideCandidate[] };
  getCanvasBoundsForNodeId?: (id: string) => Bounds | null;
  setAlignmentGuidesThrottled?: (next: AlignmentLine[]) => void;
  setDistributionSegmentsThrottled?: (next: DistributionGapSegment[]) => void;
}): {
  handleStageMouseDown: (e: Konva.KonvaEventObject<MouseEvent>) => void;
  handleStageMouseMove: (e: Konva.KonvaEventObject<MouseEvent>) => void;
  handleStageMouseUp: () => void;
  connectorNodeCommitHandledRef: { current: boolean };
} {
  const {
    stageRef,
    layerRef,
    nodeRefs,
    frameGroupRefs,
    toolRegistry,
    canWrite,
    clearActiveComment,
    isPanMode,
    panOnPrimaryCanvasDrag = false,
    isPanning,
    setIsPanning,
    panStart,
    setPanStart,
    isSelecting,
    setIsSelecting,
    selectionRect,
    setSelectionRect,
    setSelected,
    setUserSelection,
    placePreview,
    frameTool,
    frameManager,
    setSelectedFrameId,
    isConnecting,
    setIsConnecting,
    firstConnectNode,
    setFirstConnectNode,
    hoveredMarriageBar,
    setHoveredMarriageBar,
    hoveredTargetId,
    setHoveredTargetId,
    setHoveredTargetAnchor,
    setHoveredTargetPoint,
    setHoveredTargetAnchors,
    connectionPreview,
    setConnectionPreview,
    connectorTool,
    previewStartAnchorRef,
    previewEndAnchorRef,
    autoStartAnchorRef,
    getSnapCandidatesForSourceNode,
    nodes,
    edges,
    interactiveNodes,
    renderedEdges,
    diagramType,
    isEndpointDragging,
    effectiveDiagramType,
    commandManager,
    doc,
    canvasPluginsRef,
    connectionCommitExtra,
    ensureOrgDeps,
    isRotating,
    setIsRotating,
    rotateTargetIdRef,
    rotateStartAngleRef,
    rotateInitialRotationRef,
    rotatePreviewRef,
    rotateInnerRef,
    rotateCenterRef,
    rotateRafRef,
    rotateHandleGroupRef,
    updateRotateHandlePosition,
    scheduleDirtyForNode,
    previewViewportStage,
    getLatestViewportStage,
    alignmentGuidesEnabled = false,
    distributionGuidesEnabled = false,
    rotationStaticCandidatesRef,
    getCanvasBoundsForNodeId,
    setAlignmentGuidesThrottled,
    setDistributionSegmentsThrottled,
    commitViewportStageImmediate,
  } = input;
  const line2Field = useLabelSettingsStore((state) => state.line2Field);
  const labelGeometry: PersonLabelGeometryContext = { line2Field };
  const connectorStartedThisPressRef = useRef(false);
  const connectorPressStartPointRef = useRef<{ x: number; y: number } | null>(null);
  const connectorMovedThisPressRef = useRef(false);
  const connectorNodeCommitHandledRef = useRef(false);
  const primaryBackgroundPanRef = useRef<{ origin: { x: number; y: number }; moved: boolean } | null>(null);

  const getNodeIdFromTarget = (target: Konva.Node | null | undefined): string | null => {
    let current: Konva.Node | null | undefined = target;
    while (current) {
      for (const [nodeId, nodeRef] of nodeRefs.current.entries()) {
        if (nodeRef === current) {
          return nodeId;
        }
      }
      current = typeof current.getParent === 'function' ? current.getParent() : null;
    }
    return null;
  };

  const getNodeIdAtCanvasPoint = (canvasPos: { x: number; y: number }): string | null => {
    for (let i = interactiveNodes.length - 1; i >= 0; i -= 1) {
      const node = interactiveNodes[i];
      if (!node) continue;
      const { x, y, w, h } = resolveNodeRect(node);
      if (
        canvasPos.x >= x &&
        canvasPos.x <= x + w &&
        canvasPos.y >= y &&
        canvasPos.y <= y + h
      ) {
        return node.id;
      }
    }
    return null;
  };

  const resolveGeometryNode = (nodeId: string) => resolveConnectionGeometryNode({
    nodeId,
    visibleNodes: interactiveNodes,
    storeNodes: nodes,
    measuredBounds: getCanvasBoundsForNodeId?.(nodeId) ?? null,
  });

  const beginConnectionFromNodePoint = (
    sourceNodeId: string,
    canvasPos: { x: number; y: number },
  ): boolean => {
    const sourceNode = resolveGeometryNode(sourceNodeId);
    if (!sourceNode) {
      return false;
    }

    input.freeConnectionStartPointRef.current = null;
    setSelected(new Set([sourceNodeId]));
    setUserSelection?.([sourceNodeId]);

    if (input.beginConnectingFromNode?.(sourceNodeId, canvasPos)) {
      return true;
    }

    const closestSourceAnchor = getClosestNodeAnchor(canvasPos, sourceNode);
    setIsConnecting(true);
    setFirstConnectNode(sourceNodeId);
    autoStartAnchorRef.current = true;
    previewStartAnchorRef.current = closestSourceAnchor?.side ?? null;
    previewEndAnchorRef.current = null;
    setHoveredMarriageBar(null);
    setHoveredTargetId(null);
    setHoveredTargetAnchor(null);
    setHoveredTargetPoint(null);
    setHoveredTargetAnchors([]);

    if ((connectorTool as any).setStartAnchor) {
      (connectorTool as any).setStartAnchor(closestSourceAnchor?.side ?? null);
    }
    if ((connectorTool as any).setEndAnchor) {
      (connectorTool as any).setEndAnchor(null);
    }

    const startPoint = closestSourceAnchor?.pt ?? {
      x: sourceNode.x + (((sourceNode as any).w ?? (sourceNode as any).width ?? 60) as number) / 2,
      y: sourceNode.y + (((sourceNode as any).h ?? (sourceNode as any).height ?? 60) as number) / 2,
    };
    setConnectionPreview({
      startX: startPoint.x,
      startY: startPoint.y,
      endX: canvasPos.x,
      endY: canvasPos.y,
      visible: true,
    });
    document.body.style.cursor = 'crosshair';
    return true;
  };

  const beginConnectionFromCanvasPoint = (canvasPos: { x: number; y: number }) => {
    const targetNodeId = getNodeIdAtCanvasPoint(canvasPos);
    if (targetNodeId && beginConnectionFromNodePoint(targetNodeId, canvasPos)) {
      return;
    }

    const connectionSnapCandidates = getSnapCandidatesForSourceNode(null);
    const { best } = computeConnectionSnap(canvasPos, connectionSnapCandidates, null);

    if (best && beginConnectionFromNodePoint(best.id, canvasPos)) {
      previewStartAnchorRef.current = best.side;
      if ((connectorTool as any).setStartAnchor) {
        (connectorTool as any).setStartAnchor(best.side);
      }
      setConnectionPreview((prev) => ({
        ...prev,
        startX: best.pt.x,
        startY: best.pt.y,
      }));
      return;
    }

    setIsConnecting(true);
    autoStartAnchorRef.current = true;
    previewEndAnchorRef.current = null;
    setHoveredMarriageBar(null);
    setHoveredTargetId(null);
    setHoveredTargetAnchor(null);
    setHoveredTargetPoint(null);
    setHoveredTargetAnchors([]);

    input.freeConnectionStartPointRef.current = canvasPos;
    setFirstConnectNode(null);
    previewStartAnchorRef.current = null;
    if ((connectorTool as any).setStartAnchor) {
      (connectorTool as any).setStartAnchor(null);
    }
    setConnectionPreview({
      startX: canvasPos.x,
      startY: canvasPos.y,
      endX: canvasPos.x,
      endY: canvasPos.y,
      visible: true,
    });

    if ((connectorTool as any).setEndAnchor) {
      (connectorTool as any).setEndAnchor(null);
    }
    document.body.style.cursor = 'crosshair';
  };

  const clearHoveredConnectionTarget = () => {
    setHoveredMarriageBar(null);
    setHoveredTargetId(null);
    setHoveredTargetAnchor(null);
    setHoveredTargetPoint(null);
    setHoveredTargetAnchors([]);
    if ((connectorTool as any).setEndAnchor) {
      (connectorTool as any).setEndAnchor(null);
    }
    previewEndAnchorRef.current = null;
  };

  const updateHoveredConnectionTarget = (canvasPos: { x: number; y: number }, sourceNodeId: string | null) => {
    const hoveredNodeId = getNodeIdAtCanvasPoint(canvasPos);
    if (hoveredNodeId && hoveredNodeId !== sourceNodeId) {
      const hoveredNode = resolveGeometryNode(hoveredNodeId);
      if (hoveredNode) {
        const anchors = getNodeAnchorCandidates(hoveredNode);
        const closestAnchor = getClosestNodeAnchor(canvasPos, hoveredNode);
        if (closestAnchor) {
          setHoveredMarriageBar(null);
          setHoveredTargetId(hoveredNodeId);
          setHoveredTargetAnchor(closestAnchor.side);
          setHoveredTargetPoint(closestAnchor.pt);
          setHoveredTargetAnchors(anchors.map((anchor) => ({
            id: hoveredNodeId,
            side: anchor.side,
            pt: anchor.pt,
          })));
          if ((connectorTool as any).setEndAnchor) {
            (connectorTool as any).setEndAnchor(closestAnchor.side);
          }
          previewEndAnchorRef.current = closestAnchor.side;
          return {
            id: hoveredNodeId,
            side: closestAnchor.side,
            pt: closestAnchor.pt,
            dist: closestAnchor.dist,
          };
        }
      }
    }

    const connectionSnapCandidates = getSnapCandidatesForSourceNode(sourceNodeId);
    const { best, candidatesById } = computeConnectionSnap(canvasPos, connectionSnapCandidates, sourceNodeId);

    if (!best) {
      clearHoveredConnectionTarget();
      return null;
    }

    setHoveredMarriageBar(null);
    setHoveredTargetId(best.id);
    setHoveredTargetAnchor(best.side);
    setHoveredTargetPoint(best.pt);
    setHoveredTargetAnchors((candidatesById.get(best.id) || []).map((anchor) => ({
      id: best.id,
      side: anchor.side,
      pt: anchor.pt,
    })));
    if ((connectorTool as any).setEndAnchor) {
      (connectorTool as any).setEndAnchor(best.side);
    }
    previewEndAnchorRef.current = best.side;
    return best;
  };

  const handleStageMouseDown = (e: Konva.KonvaEventObject<MouseEvent>) => {
    connectorStartedThisPressRef.current = false;
    connectorPressStartPointRef.current = null;
    connectorMovedThisPressRef.current = false;
    connectorNodeCommitHandledRef.current = false;
    primaryBackgroundPanRef.current = null;
    // Don't start selection if clicking on transformer
    const clickedOnTransformer = e.target.parent?.className === 'Transformer';
    const waitingForConnectorStart = isConnecting && !connectionPreview.visible && !firstConnectNode;
    if (clickedOnTransformer && !waitingForConnectorStart) return;

    const stage = stageRef.current;
    if (!stage) return;
    const clickedCanvasBackground =
      e.target === e.target.getStage() || e.target.attrs.name === 'background';

    // Pan mode: ignore selection/deselect; start panning on any mouse button
    const startsPrimaryBackgroundPan = panOnPrimaryCanvasDrag
      && e.evt.button === 0
      && clickedCanvasBackground;
    if (isPanMode || startsPrimaryBackgroundPan) {
      const pos = getNativeClientPoint(e.evt as MouseEvent | TouchEvent);
      if (!pos) return;
      if (startsPrimaryBackgroundPan) {
        primaryBackgroundPanRef.current = { origin: pos, moved: false };
      }
      const latestViewport = getLatestViewportStage();
      setIsPanning(true);
      setPanStart({
        x: pos.x - latestViewport.offsetX,
        y: pos.y - latestViewport.offsetY,
      });
      document.body.style.cursor = 'grabbing';
      return;
    }

    if (e.target?.attrs?.name !== 'comment-pin') {
      clearActiveComment();
    }

    // If pen or placement tool is active, delegate and prevent selection logic
    const activeToolId = toolRegistry.getActiveTool()?.manifest.id;
    if (
      activeToolId === 'pen' ||
      activeToolId === 'shape-picker' ||
      activeToolId === 'sticky-note' ||
      activeToolId === 'comments' ||
      activeToolId === 'text' ||
      placePreview.visible
    ) {
      // Clear any existing selection state when pen tool is active
      if (isSelecting) {
        setIsSelecting(false);
        setSelectionRect({ x: 0, y: 0, width: 0, height: 0, visible: false });
      }
      // Let pen tool handlers run (they are attached at stage level)
      return;
    }

    // Right or middle mouse button pans (legacy), Space+drag now handled via overlay by toggling Pan mode
    if (e.evt.button === 2 || e.evt.button === 1) {
      const pos = getNativeClientPoint(e.evt as MouseEvent | TouchEvent);
      if (!pos) return;
      setIsPanning(true);
      const latestViewport = getLatestViewportStage();
      setPanStart({
        x: pos.x - latestViewport.offsetX,
        y: pos.y - latestViewport.offsetY,
      });
      document.body.style.cursor = 'grabbing';
      return;
    }

    // Check if frame tool is active
    if (activeToolId === 'genogram-frame') {
      const pos = stage.getPointerPosition()!;
      const transformedPos = toCanvasCoordinates({ x: stage.x(), y: stage.y() }, stage.scaleX(), pos);
      frameTool.handleMouseDown(transformedPos);
      return;
    }

    if (isConnecting && !connectionPreview.visible && !firstConnectNode) {
      const pos = stage.getPointerPosition();
      if (!pos) return;
      const canvasPos = toCanvasCoordinates(stage.position(), stage.scaleX(), pos);
      const targetNodeId = getNodeIdFromTarget(e.target) ?? getNodeIdAtCanvasPoint(canvasPos);
      if (targetNodeId && beginConnectionFromNodePoint(targetNodeId, canvasPos)) {
        connectorStartedThisPressRef.current = true;
        connectorPressStartPointRef.current = canvasPos;
        return;
      }
    }

    // If clicked on empty space (not on a frame or shape), clear both shape and frame selection
    if (clickedCanvasBackground) {
      setSelectedFrameId(null);
      frameManager.setActiveFrame(null);
      const stage = e.target.getStage()!;
      const pos = stage.getPointerPosition()!;
      const canvasPos = toCanvasCoordinates(stage.position(), stage.scaleX(), pos);

      // Preserve connect mode on empty canvas so mouse-up can commit a free-end connector.
      if (isConnecting) {
        if (!connectionPreview.visible && !firstConnectNode) {
          beginConnectionFromCanvasPoint(canvasPos);
          connectorStartedThisPressRef.current = true;
          connectorPressStartPointRef.current = canvasPos;
        }
        return;
      }
      setSelected(new Set());
      // Clear selection in awareness system for other users
      setUserSelection?.([]);

      // Viewers can dismiss inspection selection, but cannot lasso-select.
      if (!canWrite) {
        return;
      }

      setIsSelecting(true);
      setSelectionRect({
        x: canvasPos.x,
        y: canvasPos.y,
        width: 0,
        height: 0,
        visible: true,
      });
    }
    // If clicking on a frame when another is selected, ensure selection updates
    if (e.target?.attrs?.name === 'frame-rect' || e.target?.getParent()?.attrs?.name === 'frame-group') {
      const group = e.target.attrs.name === 'frame-rect' ? e.target.getParent() : e.target;
      const frameEntry = Array.from(frameGroupRefs.current.entries()).find(([, node]) => node === group);
      if (frameEntry) {
        const [frameId] = frameEntry;
        setSelectedFrameId(frameId);
        frameManager.setActiveFrame(frameId);
      }
    }
  };

  const handleStageMouseMove = (e: Konva.KonvaEventObject<MouseEvent>) => {
    const stage = stageRef.current;
    if (!stage) return;
    if (isPanMode) {
      if (isPanning) {
        const pos = getNativeClientPoint(e.evt as MouseEvent | TouchEvent);
        if (!pos) return;
        const nextX = pos.x - panStart.x;
        const nextY = pos.y - panStart.y;
        previewViewportStage({ scale: stage.scaleX(), offsetX: nextX, offsetY: nextY });
      }
      return;
    }

    // Handle rotation preview
    if (isRotating && rotateTargetIdRef.current) {
      const pos = stage.getPointerPosition();
      if (!pos) return;
      const canvasPos = toCanvasCoordinates({ x: stage.x(), y: stage.y() }, stage.scaleX(), pos);
      const { cx, cy } = rotateCenterRef.current;
      const angle = Math.atan2(canvasPos.y - cy, canvasPos.x - cx) * 180 / Math.PI;
      const rotation = rotateInitialRotationRef.current + (angle - rotateStartAngleRef.current);
      rotatePreviewRef.current = rotation;
      // rAF coalesced visual update
      if (!rotateRafRef.current) {
        rotateRafRef.current = requestAnimationFrame(() => {
          rotateRafRef.current = null;
          if (rotateInnerRef.current) {
            rotateInnerRef.current.rotation(rotatePreviewRef.current);
          }

          // Guides during rotation preview (no snapping).
          try {
            const movingId = rotateTargetIdRef.current;
            if (movingId && (alignmentGuidesEnabled || distributionGuidesEnabled)) {
              const stageScale = stage.scaleX() || 1;
              const threshold = 6 / stageScale;
              const labelActivationDistance = 50 / stageScale;
              const collinearTolerance = 10 / stageScale;
              const maxShapes = 10;
              const staticCandidates = rotationStaticCandidatesRef?.current ?? [];
              const movingBounds = getCanvasBoundsForNodeId?.(movingId) ?? null;

              const distCandidates = staticCandidates.map((c) => ({ shapeId: c.shapeId, bounds: c.bounds }));
              const nextGuides = alignmentGuidesEnabled && movingBounds
                ? AlignmentGuidesService.compute({
                  movingId,
                  movingBounds,
                  staticCandidates,
                  threshold,
                }).guides
                : [];
              const dh = distributionGuidesEnabled && movingBounds
                ? DistributionGuidesService.compute({
                  movingId,
                  movingBounds,
                  staticCandidates: distCandidates,
                  orientation: 'horizontal',
                  threshold,
                  maxShapes,
                  labelActivationDistance,
                  collinearTolerance,
                })
                : { snapDelta: { dx: 0, dy: 0 }, segments: [] };
              const dv = distributionGuidesEnabled && movingBounds
                ? DistributionGuidesService.compute({
                  movingId,
                  movingBounds,
                  staticCandidates: distCandidates,
                  orientation: 'vertical',
                  threshold,
                  maxShapes,
                  labelActivationDistance,
                  collinearTolerance,
                })
                : { snapDelta: { dx: 0, dy: 0 }, segments: [] };
              const nextSegments = distributionGuidesEnabled ? [...dh.segments, ...dv.segments] : [];

              setAlignmentGuidesThrottled?.(nextGuides);
              setDistributionSegmentsThrottled?.(nextSegments.slice(0, 30));
            } else {
              setAlignmentGuidesThrottled?.([]);
              setDistributionSegmentsThrottled?.([]);
            }
          } catch { }

          updateRotateHandlePosition(rotatePreviewRef.current);
          layerRef.current?.batchDraw();
          // Keep only relevant connectors visually in sync during rotation preview
          if (rotateTargetIdRef.current) {
            scheduleDirtyForNode(rotateTargetIdRef.current);
          }
        });
      }
      return;
    }

    // If pen or placement tool is active, let their handlers manage move
    const activeToolId = toolRegistry.getActiveTool()?.manifest.id;
    if (
      activeToolId === 'pen' ||
      activeToolId === 'shape-picker' ||
      activeToolId === 'sticky-note' ||
      activeToolId === 'comments' ||
      placePreview.visible
    ) {
      return;
    }

    // Check if frame tool is active and creating
    if (activeToolId === 'genogram-frame' && frameTool.isCreating) {
      const pos = stage.getPointerPosition()!;
      const transformedPos = toCanvasCoordinates({ x: stage.x(), y: stage.y() }, stage.scaleX(), pos);
      frameTool.handleMouseMove(transformedPos);
      return;
    }

    // Handle connection preview line following mouse OR endpoint drag preview
    if (isConnecting && !connectionPreview.visible && !firstConnectNode && !isEndpointDragging) {
      const pos = stage.getPointerPosition();
      if (!pos) return;
      const transformedPos = toCanvasCoordinates(stage.position(), stage.scaleX(), pos);
      updateHoveredConnectionTarget(transformedPos, null);
      return;
    }

    if ((isConnecting && connectionPreview.visible) || isEndpointDragging) {
      const pos = stage.getPointerPosition()!;
      const transformedPos = {
        x: (pos.x - stage.x()) / stage.scaleX(),
        y: (pos.y - stage.y()) / stage.scaleY(),
      };
      if (connectorStartedThisPressRef.current && connectorPressStartPointRef.current) {
        const dx = transformedPos.x - connectorPressStartPointRef.current.x;
        const dy = transformedPos.y - connectorPressStartPointRef.current.y;
        if (Math.hypot(dx, dy) >= 4) {
          connectorMovedThisPressRef.current = true;
        }
      }
      if (isConnecting && autoStartAnchorRef.current && firstConnectNode) {
        const sourceNode = resolveGeometryNode(firstConnectNode);
        const closestSourceAnchor = sourceNode
          ? getClosestNodeAnchor({ x: transformedPos.x, y: transformedPos.y }, sourceNode)
          : null;
        if (closestSourceAnchor) {
          previewStartAnchorRef.current = closestSourceAnchor.side;
          if ((connectorTool as any).setStartAnchor) {
            (connectorTool as any).setStartAnchor(closestSourceAnchor.side);
          }
          setConnectionPreview((prev) => ({
            ...prev,
            startX: closestSourceAnchor.pt.x,
            startY: closestSourceAnchor.pt.y,
          }));
        }
      }
      const hoveredNodeId = getNodeIdAtCanvasPoint(transformedPos);
      const hoveredNode =
        hoveredNodeId && hoveredNodeId !== firstConnectNode
          ? resolveGeometryNode(hoveredNodeId) ?? null
          : null;
      const hoveredNodeAnchors = hoveredNode ? getNodeAnchorCandidates(hoveredNode) : [];
      const hoveredNodeClosestAnchor = hoveredNode ? getClosestNodeAnchor(transformedPos, hoveredNode) : null;
      const connectionSnapCandidates = getSnapCandidatesForSourceNode(firstConnectNode);
      const { best, candidatesById } = computeConnectionSnap(
        { x: transformedPos.x, y: transformedPos.y },
        connectionSnapCandidates,
        firstConnectNode,
      );

      // Marriage bar snap (expanded span + existing child-connector verticals)
      const findMarriageBars = () => {
        const bars: Array<{
          x1: number;
          x2: number;
          y: number;
          marriageEdgeId: string;
          parent1Id: string;
          parent2Id: string;
        }> = [];
        edges.forEach((e) => {
          if (e.type !== 'marriage') return;
          const p1 = nodes.find((n) => n.id === e.from);
          const p2 = nodes.find((n) => n.id === e.to);
          if (!p1 || !p2) return;
          const getBottomCenterX = (n: any) => {
            const w = (n.rectW ?? n.width ?? n.w ?? 60) as number;
            const x0 = (n.rectX ?? n.x) as number;
            return x0 + w / 2;
          };
          const m1: any = p1;
          const m2: any = p2;
          let x1 = Math.min(getBottomCenterX(m1), getBottomCenterX(m2));
          let x2 = Math.max(getBottomCenterX(m1), getBottomCenterX(m2));
          const y = computeMarriageBaselineY(m1, m2, undefined, labelGeometry);
          // Expand with existing child verticals
          edges.forEach((ce) => {
            if (ce.type !== 'child') return;
            if (ce.from !== e.from && ce.from !== e.to) return;
            const child = nodes.find((n) => n.id === ce.to);
            if (!child) return;
            const cw = ((child as any).rectW ?? (child as any).width ?? (child as any).w ?? 60) as number;
            const cx = (((child as any).rectX ?? (child as any).x) as number) + cw / 2;
            if (cx < x1) x1 = cx;
            if (cx > x2) x2 = cx;
          });
          bars.push({ x1, x2, y, marriageEdgeId: e.id, parent1Id: e.from, parent2Id: e.to });
        });
        return bars;
      };
      const bars = findMarriageBars();
      const margin = 24;
      let bestBar: typeof hoveredMarriageBar = null;
      let snappedBarPt: { x: number; y: number } | null = null;
      bars.forEach((bar) => {
        if (transformedPos.x < bar.x1 - margin || transformedPos.x > bar.x2 + margin) return;
        if (Math.abs(transformedPos.y - bar.y) > margin) return;
        const cx = Math.max(bar.x1, Math.min(bar.x2, transformedPos.x));
        const dx = cx - transformedPos.x;
        const dy = bar.y - transformedPos.y;
        const d2 = dx * dx + dy * dy;
        if (!bestBar || d2 < ((snappedBarPt as any)?.d2 || Infinity)) {
          bestBar = bar;
          (snappedBarPt as any) = { x: cx, y: bar.y, d2 };
        }
      });
      // Also consider existing child connector verticals as entry points
      edges.forEach((ce) => {
        if (ce.type !== 'child') return;
        const parentMarriage = edges.find(
          (m) => m.type === 'marriage' && (m.from === ce.from || m.to === ce.from),
        );
        if (!parentMarriage) return;
        const child = nodes.find((n) => n.id === ce.to);
        const p1 = nodes.find((n) => n.id === parentMarriage.from);
        const p2 = nodes.find((n) => n.id === parentMarriage.to);
        if (!child || !p1 || !p2) return;
        const cw = ((child as any).rectW ?? (child as any).width ?? (child as any).w ?? 60) as number;
        const cx0 = (((child as any).rectX ?? (child as any).x) as number) + cw / 2;
        const y = computeMarriageBaselineY(p1 as any, p2 as any, undefined, labelGeometry);
        const nearVertical =
          Math.abs(transformedPos.x - cx0) <= margin && Math.abs(transformedPos.y - y) <= margin;
        if (nearVertical) {
          const bar = bars.find((b) => b.marriageEdgeId === parentMarriage.id);
          if (bar) {
            bestBar = bar;
            snappedBarPt = { x: cx0, y };
          }
        }
      });

      if (bestBar) {
        setHoveredMarriageBar(bestBar);
        // Snap preview endpoint to bar for visual feedback
        setConnectionPreview((prev) => ({
          ...prev,
          endX: (snappedBarPt as any).x,
          endY: (snappedBarPt as any).y,
        }));
        // Clear node hover to avoid anchor rings
        setHoveredTargetId(null);
        setHoveredTargetAnchor(null);
        setHoveredTargetPoint(null);
        setHoveredTargetAnchors([]);
        if ((connectorTool as any).setEndAnchor) (connectorTool as any).setEndAnchor(null);
        previewEndAnchorRef.current = null;
      } else if (hoveredNode && hoveredNodeClosestAnchor) {
        setHoveredMarriageBar(null);
        setHoveredTargetId(hoveredNode.id);
        setHoveredTargetAnchor(hoveredNodeClosestAnchor.side);
        setHoveredTargetPoint(hoveredNodeClosestAnchor.pt);
        previewEndAnchorRef.current = hoveredNodeClosestAnchor.side;
        setHoveredTargetAnchors(hoveredNodeAnchors.map((anchor) => ({
          id: hoveredNode.id,
          side: anchor.side,
          pt: anchor.pt,
        })));
        if ((connectorTool as any).setEndAnchor) (connectorTool as any).setEndAnchor(hoveredNodeClosestAnchor.side);
        if ((connectorTool as any).setEndOffset) {
          if ((hoveredNode as any).type === 'image') {
            (connectorTool as any).setEndOffset(0.5);
          } else {
            let { x: originX, y: originY, w, h } = resolveNodeRect(hoveredNode);
            if ((hoveredNode as any).type === 'freehand') {
              const pts = Array.isArray((hoveredNode as any)?.attrs?.points)
                ? ((hoveredNode as any).attrs.points as number[])
                : [];
              if (pts.length >= 4) {
                let minX = Infinity;
                let minY = Infinity;
                let maxX = -Infinity;
                let maxY = -Infinity;
                for (let i = 0; i < pts.length; i += 2) {
                  const px = pts[i] as number;
                  const py = pts[i + 1] as number;
                  if (!Number.isFinite(px) || !Number.isFinite(py)) continue;
                  if (px < minX) minX = px;
                  if (py < minY) minY = py;
                  if (px > maxX) maxX = px;
                  if (py > maxY) maxY = py;
                }
                if (
                  Number.isFinite(minX) &&
                  Number.isFinite(minY) &&
                  Number.isFinite(maxX) &&
                  Number.isFinite(maxY)
                ) {
                  originX = (hoveredNode as any).x + minX;
                  originY = (hoveredNode as any).y + minY;
                  w = Math.max(1, maxX - minX);
                  h = Math.max(1, maxY - minY);
                }
              }
            }
            let t = 0.5;
            if (hoveredNodeClosestAnchor.side === 'top' || hoveredNodeClosestAnchor.side === 'bottom') {
              t = (hoveredNodeClosestAnchor.pt.x - originX) / w;
            } else {
              t = (hoveredNodeClosestAnchor.pt.y - originY) / h;
            }
            (connectorTool as any).setEndOffset(Math.max(0, Math.min(1, t)));
          }
        }
        setConnectionPreview((prev) => ({
          ...prev,
          endX: hoveredNodeClosestAnchor.pt.x,
          endY: hoveredNodeClosestAnchor.pt.y,
        }));
      } else if (best !== null) {
        const b = best;
        setHoveredMarriageBar(null);
        setHoveredTargetId(b.id);
        setHoveredTargetAnchor(b.side);
        setHoveredTargetPoint(b.pt);
        previewEndAnchorRef.current = b.side;
        // render all anchors for chosen target
        const all: Array<{ side: 'top' | 'right' | 'bottom' | 'left'; pt: { x: number; y: number } }> =
          candidatesById.get(b.id) || [];
        setHoveredTargetAnchors(all.map((a) => ({ id: b.id, side: a.side, pt: a.pt })));
        if ((connectorTool as any).setEndAnchor) (connectorTool as any).setEndAnchor(b.side);
        if ((connectorTool as any).setEndOffset) {
          const target = nodes.find((n) => n.id === b.id);
          if (target) {
            if ((target as any).type === 'image') {
              // Image ports are fixed at side centers.
              (connectorTool as any).setEndOffset(0.5);
            } else {
            // Compute bounds; for freehand, derive from its points
            let { x: originX, y: originY, w, h } = resolveNodeRect(target);
            if ((target as any).type === 'freehand') {
              const pts = Array.isArray((target as any)?.attrs?.points)
                ? ((target as any).attrs.points as number[])
                : [];
              if (pts.length >= 4) {
                let minX = Infinity,
                  minY = Infinity,
                  maxX = -Infinity,
                  maxY = -Infinity;
                for (let i = 0; i < pts.length; i += 2) {
                  const px = pts[i] as number;
                  const py = pts[i + 1] as number;
                  if (!Number.isFinite(px) || !Number.isFinite(py)) continue;
                  if (px < minX) minX = px;
                  if (py < minY) minY = py;
                  if (px > maxX) maxX = px;
                  if (py > maxY) maxY = py;
                }
                if (
                  Number.isFinite(minX) &&
                  Number.isFinite(minY) &&
                  Number.isFinite(maxX) &&
                  Number.isFinite(maxY)
                ) {
                  // Include Konva transform offset so offsets map to the visible bounds
                  originX = (target as any).x + minX;
                  originY = (target as any).y + minY;
                  w = Math.max(1, maxX - minX);
                  h = Math.max(1, maxY - minY);
                }
              }
            }
            let t = 0.5;
            if (b.side === 'top' || b.side === 'bottom') t = (b.pt.x - originX) / w;
            else t = (b.pt.y - originY) / h;
            (connectorTool as any).setEndOffset(Math.max(0, Math.min(1, t)));
            }
          }
        }
        setConnectionPreview((prev) => ({ ...prev, endX: b.pt.x, endY: b.pt.y }));
      } else {
        clearHoveredConnectionTarget();
        if (isConnecting) setConnectionPreview((prev) => ({ ...prev, endX: transformedPos.x, endY: transformedPos.y }));
      }
      return;
    }

    if (isPanning) {
      const pos = getNativeClientPoint(e.evt as MouseEvent | TouchEvent);
      if (!pos) return;
      const primaryBackgroundPan = primaryBackgroundPanRef.current;
      if (primaryBackgroundPan && !primaryBackgroundPan.moved) {
        const distance = Math.hypot(
          pos.x - primaryBackgroundPan.origin.x,
          pos.y - primaryBackgroundPan.origin.y,
        );
        if (distance < PRIMARY_BACKGROUND_PAN_THRESHOLD_PX) return;
        primaryBackgroundPan.moved = true;
      }
      const newPos = (() => {
        const start = panStart as { x: number; y: number };
        return { x: pos.x - start.x, y: pos.y - start.y };
      })();
      previewViewportStage({
        scale: stage.scaleX(),
        offsetX: newPos.x,
        offsetY: newPos.y,
      });
      return;
    }

    if (!isSelecting) return;

    const selectionStage = e.target.getStage()!;
    const pos = selectionStage.getPointerPosition()!;
    // Transform screen coordinates to canvas coordinates
    const scale = selectionStage.scaleX();
    const stagePos = selectionStage.position();
    const canvasPos = {
      x: (pos.x - stagePos.x) / scale,
      y: (pos.y - stagePos.y) / scale,
    };

    const newRect = {
      ...selectionRect,
      width: canvasPos.x - selectionRect.x,
      height: canvasPos.y - selectionRect.y,
    };
    setSelectionRect(newRect);
  };

  const handleStageMouseUp = () => {
    const primaryBackgroundPan = primaryBackgroundPanRef.current;
    if (primaryBackgroundPan) {
      primaryBackgroundPanRef.current = null;
      setIsPanning(false);
      document.body.style.cursor = 'default';
      if (!primaryBackgroundPan.moved) {
        setSelectedFrameId(null);
        frameManager.setActiveFrame(null);
        setSelected(new Set());
        setUserSelection?.([]);
        return;
      }
      commitViewportStageImmediate(getLatestViewportStage());
      return;
    }

    if (isPanMode && isPanning) {
      setIsPanning(false);
      document.body.style.cursor = 'grab';
      const stage = stageRef.current;
      if (stage) {
        commitViewportStageImmediate(getLatestViewportStage());
      }
      return;
    }
    const freeStartPoint = input.freeConnectionStartPointRef.current;
    // Commit connector on release (onto a target node, or as a free-end connector)
    if (isConnecting && connectionPreview.visible && (firstConnectNode || freeStartPoint)) {
      if (
        connectorStartedThisPressRef.current &&
        !connectorMovedThisPressRef.current &&
        firstConnectNode &&
        !freeStartPoint &&
        !hoveredTargetId &&
        !hoveredMarriageBar
      ) {
        setConnectionPreview((prev) => ({ ...prev, endX: prev.startX, endY: prev.startY, visible: true }));
        clearHoveredConnectionTarget();
        connectorStartedThisPressRef.current = false;
        connectorPressStartPointRef.current = null;
        connectorMovedThisPressRef.current = false;
        return;
      }

      if (freeStartPoint && !firstConnectNode) {
        const endPoint = { x: connectionPreview.endX, y: connectionPreview.endY };
        const dragDistance = Math.hypot(endPoint.x - freeStartPoint.x, endPoint.y - freeStartPoint.y);

        if (!hoveredTargetId && dragDistance < 4) {
          input.freeConnectionStartPointRef.current = null;
          setConnectionPreview((prev) => ({ ...prev, visible: false }));
          setHoveredMarriageBar(null);
          setHoveredTargetId(null);
          setHoveredTargetAnchor(null);
          setHoveredTargetPoint(null);
          setHoveredTargetAnchors([]);
          previewStartAnchorRef.current = null;
          previewEndAnchorRef.current = null;
          return;
        }

        try {
          const id = `edge-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
          if (hoveredTargetId) {
            const endAnchor = previewEndAnchorRef.current || 'left';
            const targetNode = nodes.find((node) => node.id === hoveredTargetId);
            let endOffset = 0.5;
            if (targetNode) {
              const { x: originX, y: originY, w, h } = resolveNodeRect(targetNode);
              if (endAnchor === 'top' || endAnchor === 'bottom') {
                endOffset = w > 0 ? (endPoint.x - originX) / w : 0.5;
              } else {
                endOffset = h > 0 ? (endPoint.y - originY) / h : 0.5;
              }
              endOffset = Math.max(0, Math.min(1, endOffset));
            }
            getNodeMutationManager().addEdge({
              id,
              type: 'line',
              from: `__freeF:${id}`,
              to: hoveredTargetId,
              toAnchor: endAnchor,
              toOffset: endOffset,
              attrs: {
                routingType: undefined,
                fromPoint: freeStartPoint,
              },
            } as any);
          } else {
            getNodeMutationManager().addEdge({
              id,
              type: 'line',
              from: `__freeF:${id}`,
              to: `__freeT:${id}`,
              attrs: {
                routingType: 'straight',
                fromPoint: freeStartPoint,
                toPoint: endPoint,
              },
            } as any);
          }
          setSelected(new Set([id]));
          setUserSelection?.([id]);
        } catch { }
      } else if (hoveredMarriageBar) {
        // Create a child connector from the closer parent to the start node
        const bar = hoveredMarriageBar;
        const startNode = nodes.find((n) => n.id === firstConnectNode!);
        const p1 = nodes.find((n) => n.id === bar.parent1Id)!;
        const p2 = nodes.find((n) => n.id === bar.parent2Id)!;
        const centerX =
          (((startNode as any).rectX ?? (startNode as any).x) as number) +
          ((((startNode as any).rectW ?? (startNode as any).width ?? (startNode as any).w ?? 60) as number) / 2);
        const bottomCenterX = (n: any) =>
          (((n.rectX ?? n.x) as number) + (((n.rectW ?? n.width ?? n.w ?? 60) as number) / 2));
        const parentId = Math.abs(bottomCenterX(p1) - centerX) <= Math.abs(bottomCenterX(p2) - centerX) ? p1.id : p2.id;
        const id = `edge-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
        const mm = getNodeMutationManager();
        mm.addEdge({ id, type: 'child', from: parentId, to: startNode!.id, attrs: { __captionHidden: true } } as any);
      } else if (firstConnectNode && hoveredTargetId && hoveredTargetId !== firstConnectNode) {
        const sourceNode = nodes.find((node) => node.id === firstConnectNode);
        const targetNode = nodes.find((node) => node.id === hoveredTargetId);
        const startAnchor = previewStartAnchorRef.current;
        const endAnchor = previewEndAnchorRef.current;
        previewStartAnchorRef.current = null;
        const plan = planCanvasConnectionCommit({
          plugins: canvasPluginsRef.current,
          diagramType: effectiveDiagramType,
          sourceNodeId: firstConnectNode,
          targetNodeId: hoveredTargetId,
          sourceNode,
          targetNode,
          startAnchor,
          endAnchor,
          currentConnectionType: connectorTool.connectionType || 'line',
          nodes,
          edges,
          context: { toolRegistry, diagramType: effectiveDiagramType, commandManager, doc },
          extra: {
            ...(connectionCommitExtra ?? {}),
            autoStartAnchor: autoStartAnchorRef.current,
          },
        });
        executeCanvasConnectionPlan(plan, {
          connectorTool,
          commandManager,
          ensureOrgDeps,
        });
        connectorNodeCommitHandledRef.current = true;
      } else {
        // No target under cursor → create a line connector ending at a free canvas point
        try {
          const endX = connectionPreview.endX;
          const endY = connectionPreview.endY;
          const id = `edge-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
          const freeTargetId = `__free:${id}`; // sentinel so 'to' is a string but not a real shape id
          const startAnchor = previewStartAnchorRef.current || 'right';
          const sourceNode = nodes.find((node) => node.id === firstConnectNode);
          const edge: any = {
            id,
            type: sourceNode?.type === 'image' ? 'line' : ((connectorTool as any)?.connectionType || 'line'),
            from: firstConnectNode,
            to: freeTargetId,
            fromAnchor: startAnchor,
            fromOffset: 0.5,
            toAnchor: 'left',
            toOffset: 0.5,
            attrs: {
              routingType: undefined,
              toPoint: { x: endX, y: endY },
            },
          };
          executeIncompleteCanvasConnection(edge, { commandManager });
          // Immediately select the newly created connector so endpoint handles are visible
          try {
            setSelected(new Set([id]));
            setUserSelection?.([id]);
          } catch { }
        } catch { }
      }
      // Exit connect mode regardless of success
      setFirstConnectNode(null);
      setIsConnecting(false);
      setConnectionPreview((prev) => ({ ...prev, visible: false }));
      setHoveredMarriageBar(null);
      previewStartAnchorRef.current = null;
      autoStartAnchorRef.current = false;
      input.freeConnectionStartPointRef.current = null;
      document.body.style.cursor = 'default';
      try {
        toolRegistry.activateTool('genogram-selection');
      } catch { }
      connectorStartedThisPressRef.current = false;
      connectorPressStartPointRef.current = null;
      connectorMovedThisPressRef.current = false;
      return;
    }
    connectorStartedThisPressRef.current = false;
    connectorPressStartPointRef.current = null;
    connectorMovedThisPressRef.current = false;

    // Finish rotation
    if (isRotating && rotateTargetIdRef.current) {
      const id = rotateTargetIdRef.current;
      const rotation = rotatePreviewRef.current;
      const initial = rotateInitialRotationRef.current;
      setIsRotating(false);
      rotateTargetIdRef.current = null;
      rotateInnerRef.current = null;
      rotateHandleGroupRef.current = null;
      try {
        if (rotationStaticCandidatesRef) rotationStaticCandidatesRef.current = [];
        setAlignmentGuidesThrottled?.([]);
        setDistributionSegmentsThrottled?.([]);
      } catch { }
      const command = new RotateNodeCommand(id, rotation, initial);
      commandManager.execute(command);
      // Ensure visual state stays in sync immediately after commit
      requestAnimationFrame(() => {
        const kn = nodeRefs.current.get(id) as unknown as Konva.Container | undefined;
        const inner =
          (kn as any && typeof (kn as any).findOne === 'function')
            ? ((kn as any).findOne('.rotation-group') as Konva.Node)
            : undefined;
        if (inner) {
          inner.rotation(rotation);
          rotateInnerRef.current = inner;
          rotateHandleGroupRef.current = null;
          updateRotateHandlePosition(rotation);
          layerRef.current?.batchDraw();
        }
        // Recompute only relevant connectors after rotation commit as well
        if (id) {
          scheduleDirtyForNode(id);
          setTimeout(() => scheduleDirtyForNode(id), 0);
          setTimeout(() => scheduleDirtyForNode(id), 50);
        }
      });
      return;
    }

    // Check if frame tool is active and creating
    const activeToolId = toolRegistry.getActiveTool()?.manifest.id;
    if (activeToolId === 'genogram-frame' && frameTool.isCreating) {
      const bounds = frameTool.handleMouseUp();
      if (bounds) {
        // Execute frame creation command
        toolRegistry.executeTool('genogram-frame', {
          bounds,
          type: resolveFrameCreationDiagramType(diagramType),
        });
      }
      // Always exit frame tool on mouse up
      toolRegistry.deactivateTool();
      // Reset cursors explicitly
      const container = stageRef.current?.container();
      if (container?.style) container.style.cursor = 'default';
      document.body.style.cursor = 'default';
      // Prefer selection tool after frame creation
      try {
        toolRegistry.activateTool('genogram-selection');
      } catch { }
      return;
    }

    if (isPanning) {
      setIsPanning(false);
      document.body.style.cursor = 'default';
      commitViewportStageImmediate(getLatestViewportStage());
      return;
    }

    if (!isSelecting) return;

    setIsSelecting(false);
    setSelectionRect({ ...selectionRect, visible: false });

    const stage = stageRef.current;
    if (!stage) return;

    // selectionRect already contains canvas coordinates now
    const box = {
      x: Math.min(selectionRect.x, selectionRect.x + selectionRect.width),
      y: Math.min(selectionRect.y, selectionRect.y + selectionRect.height),
      width: Math.abs(selectionRect.width),
      height: Math.abs(selectionRect.height),
    };

    const intersectsRect = (
      a: { x: number; y: number; width: number; height: number },
      b: { x: number; y: number; width: number; height: number },
    ) => {
      return (
        a.x < b.x + b.width &&
        a.x + a.width > b.x &&
        a.y < b.y + b.height &&
        a.y + a.height > b.y
      );
    };

    const selected = interactiveNodes.filter((node) => {
      // Compute bounds per node type
      let left = node.x;
      let top = node.y;
      let right = node.x + (node.w ?? (node as any).width ?? 60);
      let bottom = node.y + (node.h ?? (node as any).height ?? 60);

      if (node.type === 'freehand') {
        const pts = Array.isArray((node as any)?.attrs?.points)
          ? ((node as any).attrs.points as number[])
          : [];
        if (pts.length >= 2) {
          let minX = Infinity,
            minY = Infinity,
            maxX = -Infinity,
            maxY = -Infinity;
          for (let i = 0; i < pts.length; i += 2) {
            const px = pts[i] as number;
            const py = pts[i + 1] as number;
            if (!Number.isFinite(px) || !Number.isFinite(py)) continue;
            if (px < minX) minX = px;
            if (py < minY) minY = py;
            if (px > maxX) maxX = px;
            if (py > maxY) maxY = py;
          }
          if (Number.isFinite(minX) && Number.isFinite(minY) && Number.isFinite(maxX) && Number.isFinite(maxY)) {
            const offsetX = typeof (node as any).x === 'number' ? (node as any).x : 0;
            const offsetY = typeof (node as any).y === 'number' ? (node as any).y : 0;
            left = offsetX + minX;
            top = offsetY + minY;
            right = offsetX + maxX;
            bottom = offsetY + maxY;
          }
        } else {
          // Fallback: treat as a small stroke at node position
          right = left + 1;
          bottom = top + 1;
        }
      }

      return (
        left < box.x + box.width &&
        right > box.x &&
        top < box.y + box.height &&
        bottom > box.y
      );
    });

    const selectedIds = selected.map((n) => n.id);

    const selectedEdgeIds: string[] = [];
    renderedEdges.forEach((edge) => {
      try {
        // Prefer model points in canvas coordinates for free lines (most reliable).
        if (edge.type === 'line') {
          const fromPoint = (edge.attrs as any)?.fromPoint as { x: number; y: number } | undefined;
          const toPoint = (edge.attrs as any)?.toPoint as { x: number; y: number } | undefined;
          if (fromPoint && toPoint) {
            const width = typeof (edge.attrs as any)?.width === 'number' ? (edge.attrs as any).width : 2;
            const pad = 10 + Math.max(0, width); // make thin lines easier to select
            const minX = Math.min(fromPoint.x, toPoint.x) - pad;
            const minY = Math.min(fromPoint.y, toPoint.y) - pad;
            const maxX = Math.max(fromPoint.x, toPoint.x) + pad;
            const maxY = Math.max(fromPoint.y, toPoint.y) + pad;
            const lineRect = { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
            if (intersectsRect(lineRect, box)) {
              selectedEdgeIds.push(edge.id);
              return;
            }
          }
        }
      } catch {
        // ignore failures
      }
    });

    const nextSelected = new Set<string>([...selectedIds, ...selectedEdgeIds]);
    setSelected(nextSelected);
    // Update awareness system for rectangle selection
    setUserSelection?.(Array.from(nextSelected));
  };

  return { handleStageMouseDown, handleStageMouseMove, handleStageMouseUp, connectorNodeCommitHandledRef };
}
